import React from 'react';
import {
    Box,
    Typography,
    Chip,
    IconButton,
    Stack,
    alpha,
    useTheme,
    Tooltip,
} from '@mui/material';
import {
    AccountCircle as AccountIcon,
    MoreVert as MoreIcon,
    WhatsApp as WhatsAppIcon,
    Instagram as InstagramIcon,
    YouTube as YouTubeIcon,
    Facebook as FacebookIcon,
    Twitter as TwitterIcon,
    Star as StarIcon,
    Error as ErrorIcon,
    SmartToy as AiIcon,
} from '@mui/icons-material';
import { STATUS_CONFIG } from './CRMConstants';

const PLATFORM_ICONS = {
    instagram: { icon: InstagramIcon, color: '#E1306C' },
    youtube:   { icon: YouTubeIcon,   color: '#FF0000' },
    facebook:  { icon: FacebookIcon,  color: '#1877F2' },
    twitter:   { icon: TwitterIcon,   color: '#1DA1F2' },
};

const LeadCard = ({ lead, onMenuOpen }) => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';

    const isOverdue = (lead) => {
        if (['closed', 'won', 'lost'].includes(lead.status)) return false;
        if (!lead.followUpAt) return false;
        const d = lead.followUpAt?.toDate ? lead.followUpAt.toDate()
            : lead.followUpAt?.seconds ? new Date(lead.followUpAt.seconds * 1000)
                : new Date(lead.followUpAt);
        return !isNaN(d.getTime()) && d < new Date();
    };

    const overdue = isOverdue(lead);
    const config = STATUS_CONFIG[lead.status] || STATUS_CONFIG.new;
    const StatusIcon = config.icon;

    const platformKey = (lead.platform || '').toLowerCase();
    const platformInfo = PLATFORM_ICONS[platformKey];
    const color = platformInfo?.color || theme.palette.primary.main;

    const formatDate = (dateValue) => {
        if (!dateValue) return 'N/A';
        if (dateValue.toDate && typeof dateValue.toDate === 'function') {
            return dateValue.toDate().toLocaleDateString();
        }
        if (dateValue.seconds) {
            return new Date(dateValue.seconds * 1000).toLocaleDateString();
        }
        const date = new Date(dateValue);
        return isNaN(date.getTime()) ? 'Invalid Date' : date.toLocaleDateString();
    };

    const handleInstagramClick = () => {
        if (lead.userProfileUrl) {
            window.open(lead.userProfileUrl, '_blank');
        } else if (lead.username) {
            window.open(`https://instagram.com/${lead.username}`, '_blank');
        } else {
            alert('No Instagram profile available for this lead.');
        }
    };

    const handleWhatsAppClick = () => {
        alert('WhatsApp contact integration coming soon!');
    };

    // AI status
    const aiStatus = lead.reply_status === 'success' ? 'replied'
        : lead.reply_status?.startsWith('failed') ? 'failed' : 'draft';
    const aiColor = aiStatus === 'replied' ? '#10b981' : aiStatus === 'failed' ? '#ef4444' : color;

    return (
        <Box sx={{
            position: 'relative',
            borderRadius: '14px',
            overflow: 'hidden',
            border: `1.5px solid ${alpha(color, overdue ? 0.4 : (isDark ? 0.2 : 0.15))}`,
            background: isDark
                ? `linear-gradient(145deg, ${alpha(color, 0.06)} 0%, ${alpha('#0f172a', 0.9)} 100%)`
                : `linear-gradient(145deg, ${alpha(color, 0.04)} 0%, #fff 100%)`,
            backdropFilter: 'blur(16px)',
            transition: 'all 0.25s ease',
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: `0 16px 40px -8px ${alpha(color, 0.3)}`,
                borderColor: alpha(color, 0.45),
            },
        }}>
            {/* Ambient glow blob */}
            <Box sx={{
                position: 'absolute', top: -40, right: -40,
                width: 120, height: 120, borderRadius: '50%',
                background: `radial-gradient(circle, ${alpha(color, 0.15)} 0%, transparent 70%)`,
                pointerEvents: 'none',
            }} />

            {/* Overdue / High Value badges */}
            {overdue && (
                <Box sx={{
                    position: 'absolute', top: 12, right: 12, zIndex: 2,
                    px: 1.2, py: 0.4, borderRadius: 99,
                    bgcolor: alpha('#ef4444', 0.15),
                    border: `1px solid ${alpha('#ef4444', 0.3)}`,
                    display: 'flex', alignItems: 'center', gap: 0.5,
                }}>
                    <ErrorIcon sx={{ fontSize: 11, color: '#ef4444' }} />
                    <Typography sx={{ fontSize: '0.6rem', fontWeight: 800, color: '#ef4444', letterSpacing: '0.06em' }}>
                        OVERDUE
                    </Typography>
                </Box>
            )}

            <Box sx={{ p: 2.5, flex: 1, display: 'flex', flexDirection: 'column' }}>
                {/* Header: Platform icon + username + menu */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0, flex: 1 }}>
                        <Box sx={{
                            width: 38, height: 38, borderRadius: 2.5,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            bgcolor: alpha(color, 0.14),
                            border: `1px solid ${alpha(color, 0.25)}`,
                            flexShrink: 0,
                        }}>
                            {platformInfo
                                ? React.createElement(platformInfo.icon, { sx: { fontSize: 20, color } })
                                : <AccountIcon sx={{ fontSize: 20, color }} />
                            }
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                            <Typography sx={{ fontWeight: 900, fontSize: '0.9rem', color: 'text.primary', lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {lead.username ? `@${lead.username}` : 'Potential Customer'}
                            </Typography>
                            <Typography sx={{ fontSize: '0.65rem', color: alpha(color, 0.8), fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                                via {lead.platform ? (lead.platform.charAt(0).toUpperCase() + lead.platform.slice(1)) : 'Unknown'} • {formatDate(lead.createdAt)}
                            </Typography>
                        </Box>
                    </Box>
                    <IconButton size="small" onClick={(e) => onMenuOpen(e, lead)} sx={{ flexShrink: 0 }}>
                        <MoreIcon fontSize="small" />
                    </IconButton>
                </Box>

                {/* Divider */}
                <Box sx={{ height: '1px', bgcolor: alpha(color, 0.1), mb: 1.5 }} />

                {/* Comment quote */}
                <Box sx={{
                    p: 1.5, borderRadius: 2,
                    bgcolor: alpha(color, isDark ? 0.06 : 0.04),
                    border: `1px solid ${alpha(color, 0.08)}`,
                    borderLeft: `3px solid ${alpha(color, 0.5)}`,
                    mb: 2,
                    minHeight: 44,
                }}>
                    <Typography variant="body2" sx={{
                        fontStyle: 'italic',
                        fontSize: '0.82rem',
                        color: 'text.secondary',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        lineHeight: 1.5,
                    }}>
                        &ldquo;{lead.comment_text}&rdquo;
                    </Typography>
                </Box>

                {/* Status chips */}
                <Stack direction="row" spacing={0.75} sx={{ mb: 2, flexWrap: 'wrap', gap: 0.75 }}>
                    <Chip
                        icon={<StatusIcon overdue={overdue} />}
                        label={config.label}
                        size="small"
                        sx={{
                            fontWeight: 700, fontSize: '0.68rem', borderRadius: 99, height: 24,
                            bgcolor: alpha(theme.palette[config.color].main, 0.12),
                            color: theme.palette[config.color].main,
                            border: `1px solid ${alpha(theme.palette[config.color].main, 0.25)}`,
                            '& .MuiChip-icon': { color: 'inherit' },
                        }}
                    />
                    {lead.is_high_value && (
                        <Chip
                            icon={<StarIcon sx={{ fontSize: 12 }} />}
                            label="HIGH VALUE"
                            size="small"
                            sx={{
                                fontWeight: 800, fontSize: '0.6rem', borderRadius: 99, height: 24,
                                bgcolor: alpha('#f59e0b', 0.12),
                                color: '#f59e0b',
                                border: `1px solid ${alpha('#f59e0b', 0.3)}`,
                                '& .MuiChip-icon': { color: 'inherit' },
                            }}
                        />
                    )}
                    {lead.deal_value > 0 && (
                        <Chip
                            label={`$${lead.deal_value}`}
                            size="small"
                            sx={{
                                fontWeight: 800, fontSize: '0.68rem', borderRadius: 99, height: 24,
                                bgcolor: alpha('#10b981', 0.12),
                                color: '#10b981',
                                border: `1px solid ${alpha('#10b981', 0.25)}`,
                            }}
                        />
                    )}
                    {lead.followUpAt && (
                        <Chip
                            label={`Follow up: ${formatDate(lead.followUpAt)}`}
                            size="small"
                            sx={{
                                fontWeight: 600, fontSize: '0.65rem', borderRadius: 99, height: 24,
                                bgcolor: alpha(overdue ? '#ef4444' : color, 0.1),
                                color: overdue ? '#ef4444' : 'text.secondary',
                                border: `1px solid ${alpha(overdue ? '#ef4444' : color, 0.2)}`,
                            }}
                        />
                    )}
                </Stack>

                {/* Spacer */}
                <Box sx={{ flex: 1 }} />

                {/* AI status + action buttons */}
                <Box sx={{ height: '1px', bgcolor: alpha(color, 0.08), mb: 1.5 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0, flex: 1 }}>
                        <AiIcon sx={{ fontSize: 14, color: aiColor }} />
                        <Typography sx={{
                            fontSize: '0.7rem', fontWeight: 700, color: aiColor,
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}>
                            {aiStatus === 'replied' ? 'AI Replied' : aiStatus === 'failed' ? 'AI Failed' : 'AI Draft'}:
                            {' '}{String(lead.ai_response || 'No reply').substring(0, 25)}…
                        </Typography>
                    </Box>
                    <Stack direction="row" spacing={0.5}>
                        <Tooltip title="Instagram DM">
                            <IconButton size="small" onClick={handleInstagramClick}
                                sx={{ width: 30, height: 30, bgcolor: alpha(color, 0.08), '&:hover': { bgcolor: alpha(color, 0.15) } }}>
                                <InstagramIcon sx={{ fontSize: 15, color }} />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="WhatsApp">
                            <IconButton size="small" onClick={handleWhatsAppClick}
                                sx={{ width: 30, height: 30, bgcolor: alpha('#25d366', 0.08), '&:hover': { bgcolor: alpha('#25d366', 0.15) } }}>
                                <WhatsAppIcon sx={{ fontSize: 15, color: '#25d366' }} />
                            </IconButton>
                        </Tooltip>
                    </Stack>
                </Box>
            </Box>

            {/* Bottom accent bar */}
            <Box sx={{
                height: 3,
                background: `linear-gradient(90deg, ${color}, ${alpha(color, 0.3)})`,
            }} />
        </Box>
    );
};

export default LeadCard;
